import React, { PropsWithChildren, ReactNode } from 'react';
import {
  Dialog as DialogUI,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/src/utils';
import Typo from '../Typo';
import LucideIcon from '../Icons/LucideIcon';
import { ScrollArea } from '../../ui/scroll-area';
import { twJoin } from 'tailwind-merge';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string | ReactNode;
  footer?: ReactNode;
  isCloseButton?: boolean;
  className?: string;

  rootProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>;
  contentProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;
  headerProps?: React.HTMLAttributes<HTMLDivElement>;
  footerProps?: React.HTMLAttributes<HTMLDivElement>;
  titleProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>;
  overlayClassName?: string;
}

function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  isCloseButton = true,
  rootProps,
  contentProps,
  headerProps,
  titleProps,
  footerProps,
  className,
  overlayClassName,
}: PropsWithChildren<DialogProps>) {
  const renderTitle = () => {
    if (typeof title === 'string') {
      return <Typo typoType="h20sb">{title}</Typo>;
    }

    return title;
  };

  return (
    <DialogUI open={open} onOpenChange={onOpenChange} {...rootProps}>
      <DialogOverlay className={cn('z-dialog', overlayClassName)} />
      <DialogContent
        {...contentProps}
        className={twJoin(
          'z-dialog gap-0 rounded-2xl border border-none p-0',
          'max-w-[500px]',
          contentProps?.className,
        )}
      >
        {title && (
          <div className="flex justify-between border-b border-b-gray1 py-4 pl-6 pr-4">
            {title && (
              <DialogHeader {...headerProps}>
                <DialogTitle {...titleProps}>{renderTitle()}</DialogTitle>
              </DialogHeader>
            )}

            {isCloseButton && (
              <DialogPrimitive.Close>
                <LucideIcon name="X" size={24} color="gray9" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </div>
        )}

        <ScrollArea className="max-h-[calc(100vh-200px)] overflow-x-hidden [&>.scroll-area>div]:!flex">
          <div className={cn('w-full p-6', className)}>{children}</div>
        </ScrollArea>

        {footer && (
          <DialogFooter className=" bottom-0 block px-5 py-6" {...footerProps}>
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </DialogUI>
  );
}

export default Dialog;
